import { FieldErrors, FieldValues, UseFormSetValue } from 'react-hook-form'
import {
  Column,
  ComponentNameValueType,
  ElementType,
  EntityLayout,
  LayoutData,
  Meta,
  MultiLookupOptionType,
  Row,
} from '../types/layout.types'
import {
  componentNames,
  EXTRA_FIELDS,
  LAYOUT_TYPE_NAMESPACE,
  MODE_CONSTANTS,
  NAMESPACES,
  ROW_TYPES,
  ZIP_CONSTANTS,
} from './constants'
import { isEmpty, isEqual, isObject, startCase } from 'lodash'
import {
  addToaster,
  FieldDataByFieldNameType,
  getKeyNameOfElement,
  LayoutElementType,
  LayoutJsonDataType,
} from '@app-store'
import moment from 'moment'
import { v4 as uuid } from 'uuid'
import { hasPermission } from './permission'
import { FORM_CONSTANTS, RECORD_STATUS } from '../constants/form.constants'
import { FORMAT_PATTERN, formatPhoneNumber, NAMESPACE } from '@ui-kit'
import { removeUnnecessaryKeys } from './listing.utils'
import { convertTo24HourFormat } from './time'
import { getKeyNameForExtraFields } from '../components/SubFormRenderer'
import { formatLocalDateWithZone } from './date.utils'
import { AppDispatch } from './agent.store'

export const getDefaultValueFromMeta = (meta: Meta) => {
  return meta?.default_value ?? meta?.defaultValue ?? ''
}
export const getValidationsFromMeta = (meta: Meta) => {
  const validations = {
    ...(meta.required && {
      required: {
        value: true,
        message: NAMESPACES.REQUIRED_FIELD,
      },
    }),
    ...(meta.char_limit && {
      maxLength: {
        value: parseInt(meta.char_limit),
        message: `${NAMESPACES.VALID_DATA}. Max character of ${meta.char_limit} limit exceeded`,
      },
    }),
    ...(meta.max_value && {
      max: {
        value: parseFloat(meta.max_value),
        message: `${NAMESPACES.VALID_DATA}. Value exceeds allowed maximum value of ${meta.max_value}`,
      },
    }),
    ...(meta.min_value != null &&
      meta.min_value !== '' && {
        min: {
          value: parseFloat(String(meta.min_value)),
          message: `${NAMESPACES.VALID_DATA}. Value less than allowed minimum value of ${meta.min_value}`,
        },
      }),
  }
  return validations
}

export const doesThisRowFailValidation = (
  row: Row,
  errors?: FieldErrors<FieldValues>
): boolean => {
  if (!errors) return false

  const result = row?.columns?.some((col) => {
    return col.elements.some((el) => {
      return el?.keyName ? errors[el.keyName] !== undefined : false
    })
  })
  return result
}

const createMapToGetElementTypeByKeyName = (
  layout_data: LayoutData | LayoutJsonDataType
) => {
  const map = new Map<string, ComponentNameValueType>()
  Object.entries(layout_data?.added_fields ?? {})?.forEach(
    ([keyName, value]) => {
      const elementName = value?.ui_data_type
      map.set(keyName, elementName as ComponentNameValueType)
    }
  )

  return map
}

const getMultiSelectValuesAfterDiff = (
  intitialValues: MultiLookupOptionType[] | undefined,
  updatedValues: MultiLookupOptionType[]
): MultiLookupOptionType[] => {
  const updatedData = updatedValues.filter(
    (obj) => !(!obj?.id && obj?._state === NAMESPACES?.REMOVED)
  )
  const initialIds = intitialValues?.map((val) => val.id)
  const updatedIds = updatedData.map((val) => val.id)
  const addedIds = updatedIds.filter(
    (id) => !initialIds?.some((val) => val === id)
  )
  const removedIds = initialIds?.filter(
    (id) => !updatedIds.some((val) => val === id)
  )
  const unchangedIds = updatedIds.filter((id) =>
    initialIds?.some((val) => val === id)
  )

  const result: MultiLookupOptionType[] = []

  updatedData.forEach((updatedValue) => {
    if (addedIds.includes(updatedValue.id)) {
      const isExistingRecord =
        updatedValue.id !== null && updatedValue.id !== undefined
      result.push({
        ...updatedValue,
        _state: isExistingRecord
          ? NAMESPACES.CHANGED
          : NAMESPACES.ADDED.toLowerCase(),
      })
    } else if (unchangedIds.includes(updatedValue.id)) {
      const initialValue = intitialValues?.find(
        (val) => val?.id === updatedValue?.id
      )
      if (!isEqual(initialValue, updatedValue)) {
        result.push({ ...updatedValue }) // changed
      } else {
        result.push({ ...updatedValue, _state: 'unchanged' })
      }
    }
  })

  intitialValues?.forEach((initialValue) => {
    if (removedIds?.includes(initialValue.id)) {
      result.push({ ...initialValue, _state: NAMESPACES?.REMOVED })
    }
  })

  return result
}
// only runs on edit mode?
export const convertValuesToDiff = (
  initialValues: FieldValues,
  values: FieldValues,
  layout_data: LayoutData
): FieldValues => {
  const ElementTypeByKeyName = createMapToGetElementTypeByKeyName(layout_data)

  const result = Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      const entry = []
      const elementName = ElementTypeByKeyName.get(key)
      entry.push(key)

      if (elementName === componentNames.MULTI_LOOKUP) {
        const fallbackInitialArray = values?.['__not_needed_layoutArray__']
        const initialForMulti = initialValues[key] ?? fallbackInitialArray
        entry.push(getMultiSelectValuesAfterDiff(initialForMulti, value))
      } else if (key === 'record_status') {
        entry.push(value)
      } else if (key === 'id') {
        entry.push(value)
      } else if (elementName === componentNames.CURRENCY) {
        if (value === '') entry.push('')
        else if (initialValues[key]?.toFixed?.(2) !== value?.toFixed?.(2))
          entry.push(value)
      } else if (
        elementName === componentNames.SINGLE_LOOKUP ||
        elementName === componentNames.CATEGORY
      ) {
        if (value === '') {
          entry.push({ id: initialValues[key]?.id, _state: 'REMOVED' })
        }
        // TODO: calculate diff for single lookup for audit history.
        // if (isEqual(initialValues[key], value)) {
        //   entry.push(undefined) // because we can update the lookup values (subForm.)
        // }
        // if (initialValues[key]?.id !== value?.id) entry.push(value)
        entry.push(value) // because we can update the lookup values (subForm.)
      } else if (!isEqual(initialValues[key], values[key])) {
        entry.push(value)
      }
      return entry
    })
  )
  return result
}

export const removePhoneMasking = (value: string) => {
  const digits = value.replace(/-/g, '')

  // Validate it's exactly 10 digits
  if (/^\d{10}$/.test(digits)) {
    return `+1 ${digits}`
  }
  return ''
}

export const removeZipMasking = (value: string) => {
  const digits = value.replace(/\D/g, '')
  if (/^\d{5}(\d{4})?$/.test(digits)) {
    return digits
  }
  return ''
}

const ZIP_FIELD_KEYS = new Set(
  [
    ZIP_CONSTANTS.ZIP,
    ZIP_CONSTANTS.ZIP_CODE,
    ZIP_CONSTANTS.ZIP_CODE_LOWERCASE,
    ZIP_CONSTANTS.ZIP_CODE_CAMELCASE,
    ZIP_CONSTANTS.GLOBAL_ZIP_CODE,
    componentNames.ZIP_CODE,
  ].map((key) => key.toLowerCase())
)

const shouldUnmaskZipValue = (key: string, elementName?: string) =>
  elementName === componentNames.ZIP_CODE ||
  ZIP_FIELD_KEYS.has(key.toLowerCase())

const removeCurrencyMasking = (value: string) => {
  if (!value) return value

  const res = +value.replace(/[$,]/g, '')
  return res
}

export const modifyValuesBeforeSave = (
  values: FieldValues,
  layout_data: LayoutData | LayoutJsonDataType
): FieldValues => {
  const ElementTypeByKeyName = createMapToGetElementTypeByKeyName(layout_data)

  const result = Object.fromEntries(
    Object.entries(values).map(([key, value]) => {
      const entry = []
      const elementName = ElementTypeByKeyName.get(key)
      entry.push(key)
      if (
        elementName === componentNames.PHONE_NUMBER ||
        elementName === componentNames.FAX_NUMBER
      ) {
        entry.push(removePhoneMasking(value))
      } else if (elementName === componentNames.CURRENCY) {
        entry.push(removeCurrencyMasking(value))
      } else if (
        shouldUnmaskZipValue(key, elementName) &&
        typeof value === 'string'
      ) {
        entry.push(removeZipMasking(value))
      } else {
        entry.push(value)
      }
      return entry
    })
  )

  return result
}

export const getElementFromAddedFields = (
  el: LayoutElementType,
  entityId: string,
  added_fields?: FieldDataByFieldNameType
): ElementType => {
  // if(!added_fields) return el;

  // for this we need to find
  el.keyName ??= ''
  let addedFields: FieldDataByFieldNameType | undefined = undefined
  if (el.parent) {
    addedFields = added_fields?.[el.parent.lookupKeyName]?.added_fields
  } else {
    addedFields = added_fields
  }

  const element: ElementType = {
    ...el,
    meta: {
      ...(el.meta as Meta),
      ...(el.keyName &&
        addedFields?.[el.keyName] && {
          ...addedFields?.[el.keyName],
          options:
            addedFields?.[el.keyName]?.control_values?.map((op) => {
              return {
                label: op,
                value: op,
              }
            }) ?? [],
          label: addedFields?.[el.keyName]?.label ?? '',
          char_limit:
            addedFields?.[el.keyName]?.char_limit?.toString() === '0'
              ? null
              : addedFields?.[el.keyName]?.char_limit?.toString(),
          max_value: addedFields?.[el.keyName]?.max_value?.toString(),
          min_value: addedFields?.[el.keyName]?.min_value?.toString(),
          required: addedFields?.[el.keyName]?.required ?? false,
          description: addedFields?.[el.keyName]?.description ?? '',
          list_view_layout_id: parseInt(
            addedFields?.[el.keyName]?.list_view_layout_id ?? ''
          ),
          selected_fields: addedFields?.[el.keyName]?.selected_fields?.map(
            (obj) => {
              return {
                ...obj,
                id: obj.id ?? '',
                name: obj?.name,
                field_order: parseInt(obj?.field_order ?? '0'),
              }
            }
          ),
        }),

      canEdit: hasPermission(
        MODE_CONSTANTS.EDIT,
        entityId,
        addedFields?.[el.keyName]?.field_id ?? undefined
      ),
      canView: hasPermission(
        MODE_CONSTANTS.VIEW,
        entityId,
        addedFields?.[el.keyName]?.field_id ?? undefined
      ),
    },
    keyName: getKeyNameOfElement(el) ?? '',
    elementId: el.elementId ?? '',
    id: +(el.id ?? 0),
    elementName: el.elementName as ComponentNameValueType,
    // elementName: el.parent
    //   ? 'single line'
    //   : (el.elementName as ComponentNameValueType),
  }
  return element
}

export const elementHasPermission = (
  element: ElementType,
  mode?: (typeof MODE_CONSTANTS)[keyof typeof MODE_CONSTANTS]
): boolean => {
  if (
    element.elementName === componentNames.SINGLE_LOOKUP ||
    element.elementName === componentNames.MULTI_LOOKUP
  ) {
    return hasPermission(
      MODE_CONSTANTS.VIEW,
      element?.meta?.relationship?.target_entity_id ?? ''
    )
  }
  if (mode === MODE_CONSTANTS.VIEW && !element.meta.canView) return false
  if (mode === MODE_CONSTANTS.EDIT && !element.meta.canEdit) return false

  return true
}

export const getDisabledFromElement = (element: ElementType) => {
  if (element.parent) {
    return true
  }
  return element.meta.disabled ?? element?.isDisabled
}

// Resolves layout field metadata for a dot-separated field path.
const getFieldMetadataAtPath = (
  keyPath: string,
  addedFields: FieldDataByFieldNameType
) => {
  const segments = keyPath?.split('.') ?? []
  let field: FieldDataByFieldNameType[string] | undefined =
    addedFields?.[segments[0]]

  for (let index = 1; index < segments.length && field; index++) {
    field = field?.added_fields?.[segments[index]] ?? undefined
  }

  return field
}

// Returns true when the field path refers to a single lookup, not a category.
const isSingleLookupFieldPath = (
  keyPath: string,
  addedFields: FieldDataByFieldNameType
) =>
  getFieldMetadataAtPath(keyPath, addedFields)?.ui_data_type ===
  componentNames.SINGLE_LOOKUP

// Collects field paths whose nested single lookup has record_status INACTIVE.
export const collectInactiveLookupKeys = (
  data: FieldValues,
  addedFields: FieldDataByFieldNameType = {},
  prefix = ''
): string[] => {
  const inactiveKeys: string[] = []

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return inactiveKeys
  }

  Object.entries(data).forEach(([key, value]) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return
    }

    const path = prefix ? `${prefix}.${key}` : key

    if (!isSingleLookupFieldPath(path, addedFields)) {
      return
    }

    const record = value as FieldValues

    if (record?.record_status === RECORD_STATUS.INACTIVE) {
      inactiveKeys.push(path)
    }

    Object.entries(record).forEach(([nestedKey, nestedValue]) => {
      const nestedPath = `${path}.${nestedKey}`

      if (
        nestedKey !== 'record_status' &&
        nestedValue &&
        typeof nestedValue === 'object' &&
        !Array.isArray(nestedValue) &&
        isSingleLookupFieldPath(nestedPath, addedFields)
      ) {
        inactiveKeys.push(
          ...collectInactiveLookupKeys(
            { [nestedKey]: nestedValue } as FieldValues,
            addedFields,
            path
          )
        )
      }
    })
  })

  return inactiveKeys
}

// Resolves a user-facing label for an inactive lookup field path.
export const getInactiveLookupLabel = (
  keyPath: string,
  addedFields: FieldDataByFieldNameType
): string => {
  const directField = addedFields?.[keyPath]
  if (directField?.label) return directField.label

  const segments = keyPath?.split('.') ?? []
  if (segments.length > 1) {
    const parentField = addedFields?.[segments[0]]
    const nestedKey = segments.slice(1).join('.')
    const nestedField =
      parentField?.added_fields?.[nestedKey] ??
      parentField?.added_fields?.[segments?.[segments.length - 1]]
    if (nestedField?.label) return nestedField.label
  }

  return startCase(segments?.[segments.length - 1]?.replace(/_/g, ' ') ?? '')
}

// Builds tooltip messages keyed by inactive single-lookup field paths.
export const buildInactiveLookupTooltips = (
  inactiveKeys: string[],
  addedFields: FieldDataByFieldNameType
): Record<string, string> =>
  Object.fromEntries(
    (inactiveKeys ?? []).map((key) => [
      key,
      `This ${getInactiveLookupLabel(key, addedFields)} is currently disabled`,
    ])
  )

export const isAuditField = (element: ElementType) => {
  if (visibleAuditFields.includes(element?.meta?.field_name ?? '')) {
    return true
  }
  return false
}

export const getValidationsFromElement = (element: ElementType) => {
  if (element.parent) {
    return {}
  }

  if (
    element.elementName !== componentNames.NUMBER &&
    element.elementName !== componentNames.FILE_UPLOAD
  ) {
    delete element.meta.min_value
    delete element.meta.max_value
  }
  return getValidationsFromMeta(element.meta)
}
export const formatValueForViewMode = (
  element: ElementType,
  value: string | undefined | null | string[],
  ignorePermission = false
) => {
  if (!ignorePermission) {
    if (!element.meta.canView) return NAMESPACE.REDACTED
  }

  if (element?.elementName === componentNames?.CHECKBOX) {
    return value ? 'Yes' : 'No'
  }

  if (value === undefined || value === null) return '--'
  if (element?.elementName === componentNames?.DATE_TIME) {
    return formatLocalDateWithZone(value as string)
  } else if (element?.elementName === componentNames?.DATE) {
    return moment(value).format('MM/DD/YYYY')
  } else if (element?.elementName === componentNames?.MULTI_SELECT_DROPDOWN) {
    return (value as string[])?.length > 0
      ? (value as string[])?.join(', ')
      : '--'
  } else if (element?.elementName === componentNames?.TIME) {
    const currentDateString = new Date().toISOString().split('T')[0]
    const fullDateTime = `${currentDateString}T${
      value.toString().includes('Z') ? value : value + 'Z'
    }`
    const date = new Date(fullDateTime)
    const timezoneOffset = date.getTimezoneOffset()
    const val = new Date(date.getTime() - timezoneOffset * 60000)
      .toISOString()
      .split('.')[0]
      .split('T')[1]
    return convertTo24HourFormat(val)
  } else if (
    element?.elementName === componentNames?.PHONE_NUMBER ||
    element?.elementName === componentNames?.FAX_NUMBER
  ) {
    return formatPhoneNumber(value as string, FORMAT_PATTERN.PHONE_INPUT)
  }
  return isObject(value) ? JSON.stringify(value) : value?.toString()
}

export const removeEmptyStringValuesFromRecord = (
  values: FieldValues
): FieldValues => {
  return Object.fromEntries(Object.entries(values).filter(([_, v]) => v !== ''))
}

export const removeNullValuesFromRecord = (
  values: FieldValues
): FieldValues => {
  return Object.fromEntries(
    Object.entries(values).filter(([_, v]) => v !== null)
  )
}

export const auditFields = ['record_status']

export const visibleAuditFields = [
  'created_at',
  'created_by',
  'modified_at',
  'modified_by',
]

export const isDraftSubmission = (e: React.FormEvent<HTMLFormElement>) => {
  const submitEvent = e.nativeEvent as SubmitEvent
  return submitEvent.submitter?.id === FORM_CONSTANTS.SAVE_AS_DRAFT
}

function getDescendantProp(obj: FieldValues, desc: string) {
  return desc.split('.').reduce((a, b) => a?.[b], obj)
}

export const extractRowsFromLayoutData = (layoutData: LayoutData): Row[] => {
  const rows: Row[] = []
  layoutData.pages.forEach((page) => {
    page.rows.forEach((row) => {
      rows.push(row)
    })
  })
  return rows
}

export const isThisFieldASubForm = (
  fieldName: string,
  layoutData: LayoutData
): boolean => {
  const rows = extractRowsFromLayoutData(layoutData)
  return rows.some((row) => {
    return (
      row?.rowType === ROW_TYPES.SUB_FORM &&
      row?.lookup_meta?.lookup_keyName === fieldName
    )
  })
}

export const removeAuditFieldsAndFormatValues = (
  values: FieldValues,
  layout: LayoutData,
  mode: 'add' | 'edit' | 'view',
  initialValues?: FieldValues | null,
  globalFormattedValues?: FieldValues,
  parentKeyName?: string
): FieldValues => {
  if (!values) return values

  delete values.created_at
  delete values.modified_at
  delete values.created_by
  delete values.modified_by
  // delete values.disabled
  delete values.lookupLabels

  let formattedValues = Object.fromEntries(
    Object.entries(values).filter(
      ([_, v]) => v !== null && JSON.stringify(v) !== '{}'
    )
  )

  if (!formattedValues || JSON.stringify(formattedValues) === '{}') {
    return formattedValues
  }

  if (mode === MODE_CONSTANTS.ADD) {
    const threadsArray = Object.keys(layout?.added_fields || {}).filter(
      (key) =>
        layout?.added_fields?.[key]?.control_type === ROW_TYPES.NOTES ||
        layout?.added_fields?.[key]?.control_type === ROW_TYPES.NOTE
    )
    if (threadsArray?.length)
      threadsArray?.forEach((val) => {
        formattedValues[val] = formattedValues[val]
          ? [{ name: formattedValues[val] }]
          : ''
      })
  }

  formattedValues = modifyValuesBeforeSave(formattedValues, layout)

  formattedValues = {
    ...formattedValues,
    ...formattedValues?.['subForm'], // will only happen for parent, once.
  }

  // if it contains a sub-form
  Object.keys(layout?.added_fields ?? {}).forEach((key) => {
    if (
      layout?.added_fields?.[key]?.ui_data_type ===
        componentNames.MULTI_LOOKUP ||
      isThisFieldASubForm(key, layout)
    ) {
      // happens for subform and multilookup!
      const subFormKeyName =
        `subForm.${parentKeyName ? parentKeyName + '.' : ''}` + key
      // const type = ''
      const layoutKeyName = getKeyNameForExtraFields(subFormKeyName, '_layout')
      const initialValuesKeyName = getKeyNameForExtraFields(
        subFormKeyName,
        '_initialValues'
      )
      const layoutOfThisForm = getDescendantProp(
        globalFormattedValues ?? formattedValues,
        layoutKeyName
      )?.layout_data

      const initialValuesForThisForm = getDescendantProp(
        globalFormattedValues ?? formattedValues,
        initialValuesKeyName
      )
      if (
        layout?.added_fields?.[key]?.ui_data_type ===
        componentNames.MULTI_LOOKUP
      ) {
        if (formattedValues[key]) {
          // each array value is also a form.
          formattedValues[key] = formattedValues[key]?.map(
            (fv: FieldValues, idx: number) => {
              return removeAuditFieldsAndFormatValues(
                fv,
                layoutOfThisForm,
                mode,
                initialValuesForThisForm?.[idx],
                globalFormattedValues ?? formattedValues,
                `${parentKeyName ? parentKeyName + '.' : ''}` + key
              )?.formattedValues
            }
          )
        }
      } else {
        formattedValues[key] = removeAuditFieldsAndFormatValues(
          formattedValues?.[key],
          layoutOfThisForm,
          mode,
          initialValuesForThisForm,
          globalFormattedValues ?? formattedValues,
          `${parentKeyName ? parentKeyName + '.' : ''}` + key
        )?.formattedValues
      }
    }
  })

  const originalFormData = JSON.parse(JSON.stringify(formattedValues))
  if (mode === MODE_CONSTANTS.EDIT) {
    formattedValues = convertValuesToDiff(
      initialValues ?? {},
      formattedValues,
      layout
    )
  }

  // formattedValues = {
  //   ...formattedValues,
  //   ...(formattedValues?.['subForm'] &&
  //     Object.fromEntries(
  //       Object.entries(formattedValues?.['subForm']).map(([key, value]) => {
  // const layoutKeyName =
  //   EXTRA_FIELDS.EXTRA_FIELD + 'subForm.' + key + '_layout'
  // const layoutOfThisForm = getDescendantProp(
  //   formattedValues,
  //   layoutKeyName
  // ).layout_data
  //         debugger
  //         return [
  //           key,
  //           removeAuditFieldsAndFormatValues(
  //             value as FieldValues,
  //             layoutOfThisForm,
  //             mode
  //           ),
  //         ]
  //       })
  //     )),
  // }

  formattedValues = removeUnnecessaryKeys(
    EXTRA_FIELDS.EXTRA_FIELD,
    formattedValues
  )

  formattedValues = removeUnnecessaryKeys('disabled_', formattedValues)

  delete formattedValues['subForm']

  return { formattedValues, originalFormData }
}

export const removeSecondsFromDateTime = (dateString: string) => {
  // Backend returns in these formats
  // 2025-03-25T19:57:59.184Z
  // 2025-03-25T19:57:00Z
  // which sometimes fail in datetime-local input.

  const date = new Date(dateString)

  // Extract year, month, day, hours, and minutes
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0') // Months are 0-indexed
  const day = String(date.getDate()).padStart(2, '0')
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')

  // Format the string as "YYYY-MM-DDTHH:mm"
  return `${year}-${month}-${day}T${hours}:${minutes}`
}

export const formatDataBeforeLoad = (
  data: FieldValues,
  added_fields: FieldDataByFieldNameType
) => {
  Object.entries(added_fields).forEach(([fieldName, field]) => {
    if (
      ([componentNames.RADIO_GROUP] as string[]).includes(
        field?.ui_data_type ?? ''
      )
    ) {
      if (['float', 'number'].includes(field?.control_type ?? ' '))
        // TODO: do this inside dropdown wrapper.
        data[fieldName] = data[fieldName]?.toString()
    } else if (field?.ui_data_type === componentNames.TIME && data[fieldName]) {
      // TODO: do this inside time wrapper.
      data[fieldName] = data[fieldName].replace('Z', '') + 'Z'
    } else if (
      field?.ui_data_type === componentNames.DATE_TIME &&
      data[fieldName]
    ) {
      // TODO: do this inside datetime wrapper.
      data[fieldName] = removeSecondsFromDateTime(data[fieldName])
    } else if (field?.ui_data_type === componentNames.CHECKBOX) {
      data[fieldName] = data[fieldName] === null ? false : data[fieldName]
    }
  })
}

export const setDefaultValueForDropdown = (
  added_fields: FieldDataByFieldNameType,
  setValue: UseFormSetValue<FieldValues>
  // parentKeyName?: string
) => {
  // TODO: handle for parentKeyName OR make a wrapper.
  Object.entries(added_fields).forEach(([fieldName, field]) => {
    if (
      ([componentNames.DROPDOWN] as string[]).includes(
        field?.ui_data_type ?? ''
      )
    ) {
      if (field?.default_value) {
        setValue(fieldName, field?.default_value)
      }
    } else if (
      ([componentNames.MULTI_SELECT_DROPDOWN] as string[]).includes(
        field?.ui_data_type ?? ''
      )
    ) {
      if (field?.default_value && typeof field?.default_value === 'string') {
        const defaultValue = field?.default_value.split(',')
        setValue(fieldName, defaultValue)
      } else {
        setValue(fieldName, field?.default_value)
      }
    }
  })
}
const mapNestedLayoutWithParentKeyName = (
  column: Column,
  parentKeyName: string
) => {
  return column.elements.map((element) => {
    return {
      ...element,
      keyName: `${parentKeyName}.${element.keyName}`,
    }
  })
}
export const formatLayoutWithParentKeyName = (
  fullLayout: EntityLayout,
  parentKeyName: string
) => {
  return {
    ...fullLayout,
    layout_data: {
      ...fullLayout.layout_data,
      added_fields: Object.fromEntries(
        Object.entries(fullLayout.layout_data.added_fields ?? {}).map(
          ([key, value]) => {
            const newKey = `${parentKeyName}.${key}`
            return [newKey, value]
          }
        )
      ),
      pages: fullLayout.layout_data.pages.map((page) => {
        return {
          ...page,
          rows: page.rows.map((row) => {
            return {
              ...row,
              columns: row.columns.map((column) => {
                return {
                  ...column,
                  elements: mapNestedLayoutWithParentKeyName(
                    column,
                    parentKeyName
                  ),
                }
              }),
            }
          }),
        }
      }),
    },
  }
}

export const getSiblingElementKeyName = (
  element: ElementType,
  siblingToFind: string
) => {
  const keyName = element?.keyName
  const parentKeyName = keyName?.split('.').slice(0, -1).join('.')
  const siblingKeyName =
    parentKeyName.trim() === ''
      ? siblingToFind
      : `${parentKeyName}.${siblingToFind}`
  return siblingKeyName
}

export const getTypeOfLayoutByMode = (
  mode: (typeof MODE_CONSTANTS)[keyof typeof MODE_CONSTANTS]
) => {
  return mode === MODE_CONSTANTS.VIEW
    ? `${LAYOUT_TYPE_NAMESPACE.DEFAULT_OVERVIEW},${LAYOUT_TYPE_NAMESPACE.TABS_TYPE}`
    : LAYOUT_TYPE_NAMESPACE.DEFAULT_VIEW_EDIT
}
export const getTypeOfLayoutByModeForService = (
  mode: (typeof MODE_CONSTANTS)[keyof typeof MODE_CONSTANTS]
) => {
  switch (mode) {
    case MODE_CONSTANTS.ADD:
    case MODE_CONSTANTS.EDIT:
      return LAYOUT_TYPE_NAMESPACE.DEFAULT_VIEW_EDIT

    case MODE_CONSTANTS.VIEW:
      return LAYOUT_TYPE_NAMESPACE.TABS_TYPE

    default:
      return LAYOUT_TYPE_NAMESPACE.DEFAULT_OVERVIEW
  }
}

export const isThisAParentCaseIdHyperLink = (element: ElementType) => {
  const isSingleLookup = element?.elementName === componentNames?.SINGLE_LOOKUP
  const selectedFields = element?.meta?.selected_fields
  const hasOneSelectedField = selectedFields?.length === 1
  const isUniqueId =
    selectedFields?.[0]?.ui_data_type === componentNames?.UNIQUE_ID
  const isParentCaseId = element?.keyName === NAMESPACES.PARENT_CASE_ID

  return isSingleLookup && hasOneSelectedField && isUniqueId && isParentCaseId
}

export const removeParentKeyName = (keyName: string) => {
  return keyName.split('.')?.slice(-1)?.[0]
}

export const renderValuesWithDelimiter = (
  values: (string | undefined)[],
  delimiter = ' | '
) => {
  const filteredValues = values.filter(
    (val) => val !== undefined && val !== null && val !== ''
  )
  if (filteredValues.length === 0) return ''
  return filteredValues.join(delimiter)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObject = Record<string, any>

export function flattenObject<T extends AnyObject>(
  obj: T,
  parentKey = '',
  result: AnyObject = {},
  separator = '.'
): AnyObject {
  for (const key in obj) {
    if (!Object.prototype.hasOwnProperty.call(obj, key)) continue

    const newKey = parentKey ? `${parentKey}${separator}${key}` : key
    const value = obj[key]

    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      flattenObject(value, newKey, result, separator) // Recursive flatten
    } else {
      result[newKey] = value
    }
  }
  return result
}
export function capitalizeWord(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
}
// Values an untouched inline address/phone row is pre-filled with by its layout
// (dropdown default, default country); on their own they are not user input.
const PREFILLED_SUB_FORM_KEYS = ['record_status', 'country']

const hasUserValue = (value: unknown): boolean => {
  if (value == null || value === '' || value === false) return false
  if (typeof value === 'object') return Object.values(value).some(hasUserValue)
  return true
}

const isUntouchedSubForm = (rows: FieldValues | FieldValues[]): boolean =>
  (Array.isArray(rows) ? rows : [rows]).every((row) =>
    Object.entries(row ?? {}).every(
      ([key, value]) =>
        PREFILLED_SUB_FORM_KEYS.includes(key) || !hasUserValue(value)
    )
  )
export const showToasterMethod = (message: string, dispatch: AppDispatch) => {
  dispatch(
    addToaster({
      message,
      type: 'error',
      id: uuid(),
    })
  )
}
export const validatePhoneNumberAndAddress = (
  values: FieldValues,
  dispatch: AppDispatch,
  cleanedValues?: FieldValues
) => {
  let flag = false
  // for patient address and contact number validation
  if (values?.[NAMESPACES.C_PATIENT_ADDRESS]) {
    const tempData = values[NAMESPACES.C_PATIENT_ADDRESS]

    const shouldSkip = isUntouchedSubForm(tempData)

    if (shouldSkip) {
      delete values[NAMESPACES.C_PATIENT_ADDRESS]
      if (cleanedValues) {
        delete cleanedValues[NAMESPACES.C_PATIENT_ADDRESS]
      }
    } else {
      const address = tempData?.[0] || {}
      const missingFields = []

      if (isEmpty(address.zip_code)) missingFields.push(NAMESPACES.ZIP_CODE)
      if (isEmpty(address.city)) missingFields.push(NAMESPACES.CITY)
      if (isEmpty(address.state)) missingFields.push(NAMESPACES.STATE_ADDRESS)

      if (missingFields.length > 0) {
        flag = true
        showToasterMethod(
          `'${missingFields.join(', ')}' ${NAMESPACES.SAVE_ADDRESS}`,
          dispatch
        )
      }
    }
  }

  if (values?.[NAMESPACES.C_PATIENT_PHONE]) {
    const tempData = values[NAMESPACES.C_PATIENT_PHONE]

    const shouldSkip = isUntouchedSubForm(tempData)

    if (shouldSkip) {
      delete values[NAMESPACES.C_PATIENT_PHONE]
      if (cleanedValues) {
        delete cleanedValues[NAMESPACES.C_PATIENT_PHONE]
      }
    } else {
      const phone = tempData?.[0] || {}
      const missingFields = []

      if (isEmpty(phone.phone_type)) {
        missingFields.push(NAMESPACES.PHONE_TYPE)
      }
      if (isEmpty(phone.phone_number)) {
        missingFields.push(NAMESPACES.PHONE_NUMBER)
      }

      if (missingFields.length > 0) {
        flag = true
        showToasterMethod(
          `'${missingFields.join(', ')}' ${NAMESPACES.SAVE_CONTACT_INFO}`,
          dispatch
        )
      }
    }
  }
  // for provider contact number validation
  if (values?.[NAMESPACES.C_PROVIDER_CONTACT]) {
    const tempData = values[NAMESPACES.C_PROVIDER_CONTACT]

    const shouldSkip = isUntouchedSubForm(tempData)

    if (shouldSkip) {
      delete values[NAMESPACES.C_PROVIDER_CONTACT]
      if (cleanedValues) {
        delete cleanedValues[NAMESPACES.C_PROVIDER_CONTACT]
      }
    } else {
      const phone = tempData?.[0] || {}
      const missingFields = []

      if (isEmpty(phone.phone_type)) {
        missingFields.push(NAMESPACES.PHONE_TYPE)
      }
      if (isEmpty(phone.phone_number)) {
        missingFields.push(NAMESPACES.PHONE_NUMBER)
      }

      if (missingFields.length > 0) {
        flag = true
        showToasterMethod(
          `'${missingFields.join(', ')}' ${NAMESPACES.SAVE_CONTACT_INFO}`,
          dispatch
        )
      }
    }
  }
  // for contact address and contact validation
  if (values?.[NAMESPACES.C_CONTACT_ADDRESS]) {
    const tempData = values[NAMESPACES.C_CONTACT_ADDRESS]

    const shouldSkip = isUntouchedSubForm(tempData)

    if (shouldSkip) {
      delete values[NAMESPACES.C_CONTACT_ADDRESS]
      if (cleanedValues) {
        delete cleanedValues[NAMESPACES.C_CONTACT_ADDRESS]
      }
    } else {
      const address = tempData?.[0] || {}
      const missingFields = []

      if (isEmpty(address.zip_code)) missingFields.push(NAMESPACES.ZIP_CODE)
      if (isEmpty(address.city)) missingFields.push(NAMESPACES.CITY)
      if (isEmpty(address.state)) missingFields.push(NAMESPACES.STATE_ADDRESS)

      if (missingFields.length > 0) {
        flag = true
        showToasterMethod(
          `'${missingFields.join(', ')}' ${NAMESPACES.SAVE_ADDRESS}`,
          dispatch
        )
      }
    }
  }
  if (values?.[NAMESPACES.C_CONTACT_PHONE]) {
    const tempData = values[NAMESPACES.C_CONTACT_PHONE]

    const shouldSkip = isUntouchedSubForm(tempData)

    if (shouldSkip) {
      delete values[NAMESPACES.C_CONTACT_PHONE]
      if (cleanedValues) {
        delete cleanedValues[NAMESPACES.C_CONTACT_PHONE]
      }
    } else {
      const phone = tempData?.[0] || {}
      const missingFields = []

      if (isEmpty(phone.phone_type)) {
        missingFields.push(NAMESPACES.PHONE_TYPE)
      }
      if (isEmpty(phone.phone_number)) {
        missingFields.push(NAMESPACES.PHONE_NUMBER)
      }

      if (missingFields.length > 0) {
        flag = true
        showToasterMethod(
          `'${missingFields.join(', ')}' ${NAMESPACES.SAVE_CONTACT_INFO}`,
          dispatch
        )
      }
    }
  }
  return flag
}
export const enhanceWithState = (obj: FieldValues): FieldValues => {
  if (Array.isArray(obj)) {
    return obj.map((item) => enhanceWithState(item) as FieldValues)
  }

  if (obj && typeof obj === 'object') {
    return {
      ...Object.fromEntries(
        Object.entries(obj).map(([key, val]) => [key, enhanceWithState(val)])
      ),
      _state: obj._state || NAMESPACES.ADDED,
    }
  }

  return obj
}

export { getKeyNameForDisabledFieldsOfSingleLookup } from '@app-store'
